/* ---------------------------------------------------------------
   Expanding gallery
   Layouts: stack | slide | orbit | shelf | flat

   - stack / slide / orbit : each panel gets data-offset, a circular
     distance from the active panel (clamped to ±3), and CSS does
     the rest.
   - shelf                 : panels keep their place in the crate;
     the active one gets .is-active and its caption is mirrored in
     .shelf-captions.
   - flat                  : original behaviour, .active on hover
     (desktop) or tap.

   Every layout also gets data-direction (forward | back) on the host and
   .is-leaving on the panel being replaced, so a layout can send a product
   out one way and bring the next one in from the other.
--------------------------------------------------------------- */
(function () {
  'use strict';

  var OFFSET_LAYOUTS = ['stack', 'slide', 'orbit'];
  var MAX_OFFSET = 3;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class ExpandingGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.panels = Array.prototype.slice.call(this.querySelectorAll('.gallery-panel'));
      if (!this.panels.length) return;

      this.initialised = true;

      this.captions = Array.prototype.slice.call(this.querySelectorAll('[data-caption-index]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-dot]'));
      this.prevButton = this.querySelector('[data-gallery-prev]');
      this.nextButton = this.querySelector('[data-gallery-next]');

      var layoutMatch = this.className.match(/layout-([a-z]+)/);
      this.layout = layoutMatch ? layoutMatch[1] : 'stack';
      this.usesOffsets = OFFSET_LAYOUTS.indexOf(this.layout) !== -1;
      this.isShelf = this.layout === 'shelf';
      this.isFlat = this.layout === 'flat';

      this.index = 0;
      this.timer = null;
      this.paused = false;
      this.visible = true;

      this.autoRotate = this.getAttribute('auto-rotate') === 'true' && !prefersReducedMotion();
      this.rotateInterval = Math.max(
        2000,
        parseInt(this.getAttribute('rotate-interval'), 10) || 5000
      );

      this.bindHandlers();
      this.addListeners();
      this.select(0, true);
      this.watchVisibility();
      this.startAuto();
    }

    disconnectedCallback() {
      window.clearTimeout(this.leavingTimer);
      this.stopAuto();
      this.removeListeners();
      if (this.observer) this.observer.disconnect();
      this.initialised = false;
    }

    /* ---------- setup ---------- */

    bindHandlers() {
      this.handlePanelClick = this.handlePanelClick.bind(this);
      this.handlePanelKey = this.handlePanelKey.bind(this);
      this.handlePanelEnter = this.handlePanelEnter.bind(this);
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleDot = this.handleDot.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePause = this.handlePause.bind(this);
      this.handleHover = this.handleHover.bind(this);
      this.handleResume = this.handleResume.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleDocumentVisibility = this.handleDocumentVisibility.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    }

    addListeners() {
      var self = this;

      this.panels.forEach(function (panel) {
        panel.addEventListener('click', self.handlePanelClick);
        panel.addEventListener('keydown', self.handlePanelKey);
        if (self.isFlat) panel.addEventListener('mouseenter', self.handlePanelEnter);
      });

      if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.handleNext);

      this.dots.forEach(function (dot) {
        dot.addEventListener('click', self.handleDot);
      });

      this.addEventListener('keydown', this.handleKeydown);
      // mouseover bubbles, so this fires again for every element the pointer
      // crosses — which is what lets the pause follow what's under it rather
      // than latching on for the whole container.
      this.addEventListener('mouseover', this.handleHover);
      this.addEventListener('mouseleave', this.handleResume);
      this.addEventListener('focusin', this.handlePause);
      this.addEventListener('focusout', this.handleResume);
      this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.addEventListener('touchend', this.handleTouchEnd, { passive: true });

      document.addEventListener('visibilitychange', this.handleDocumentVisibility);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    removeListeners() {
      var self = this;

      this.panels.forEach(function (panel) {
        panel.removeEventListener('click', self.handlePanelClick);
        panel.removeEventListener('keydown', self.handlePanelKey);
        panel.removeEventListener('mouseenter', self.handlePanelEnter);
      });

      if (this.prevButton) this.prevButton.removeEventListener('click', this.handlePrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.handleNext);

      this.dots.forEach(function (dot) {
        dot.removeEventListener('click', self.handleDot);
      });

      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('mouseover', this.handleHover);
      this.removeEventListener('mouseleave', this.handleResume);
      this.removeEventListener('focusin', this.handlePause);
      this.removeEventListener('focusout', this.handleResume);
      this.removeEventListener('touchstart', this.handleTouchStart);
      this.removeEventListener('touchend', this.handleTouchEnd);

      document.removeEventListener('visibilitychange', this.handleDocumentVisibility);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    watchVisibility() {
      if (!('IntersectionObserver' in window)) return;
      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.visible = entry.isIntersecting;
            if (self.visible) self.startAuto();
            else self.stopAuto();
          });
        },
        { threshold: 0.15 }
      );
      this.observer.observe(this);
    }

    /* ---------- state ---------- */

    select(index, immediate) {
      var total = this.panels.length;
      if (!total) return;

      var previousIndex = this.index;
      this.index = ((index % total) + total) % total;

      // Which way the gallery travelled, taking the wrap into account, so a
      // layout can animate forward and back along opposite paths. The panel
      // being left behind is flagged for the length of its transition.
      if (previousIndex !== this.index) {
        var step = this.index - previousIndex;
        if (step < 0) step += total;

        this.setAttribute('data-direction', step * 2 <= total ? 'forward' : 'back');

        var leaving = this.panels[previousIndex];
        if (leaving) {
          window.clearTimeout(this.leavingTimer);
          this.panels.forEach(function (panel) {
            panel.classList.remove('is-leaving');
          });
          leaving.classList.add('is-leaving');

          this.leavingTimer = window.setTimeout(function () {
            leaving.classList.remove('is-leaving');
          }, 1400);
        }
      } else if (!this.hasAttribute('data-direction')) {
        this.setAttribute('data-direction', 'forward');
      }

      var self = this;

      this.panels.forEach(function (panel, i) {
        var isActive = i === self.index;

        if (isActive) panel.classList.remove('is-leaving');

        if (self.usesOffsets) {
          panel.setAttribute('data-offset', String(self.offsetFor(i, total)));
        }

        panel.classList.toggle('is-active', isActive);
        panel.classList.toggle('active', isActive && self.isFlat);
        panel.setAttribute('aria-current', isActive ? 'true' : 'false');

        if (self.isShelf) {
          panel.setAttribute('tabindex', '0');
        }
      });

      this.captions.forEach(function (caption) {
        var i = parseInt(caption.getAttribute('data-caption-index'), 10);
        caption.classList.toggle('is-active', i === self.index);
        caption.setAttribute('aria-hidden', i === self.index ? 'false' : 'true');
      });

      // The showcase layout only shows the product that comes next, so dots
      // carry an is-next flag as well as is-active.
      var nextIndex = (this.index + 1) % total;

      this.dots.forEach(function (dot) {
        var i = parseInt(dot.getAttribute('data-gallery-dot'), 10);
        var isActive = i === self.index;
        dot.classList.toggle('is-active', isActive);
        dot.classList.toggle('is-next', i === nextIndex && !isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      if (!immediate) this.restartAuto();
    }

    offsetFor(i, total) {
      var diff = i - this.index;
      if (diff > total / 2) diff -= total;
      if (diff < -total / 2) diff += total;
      if (diff > MAX_OFFSET) diff = MAX_OFFSET;
      if (diff < -MAX_OFFSET) diff = -MAX_OFFSET;
      return diff;
    }

    next() {
      this.select(this.index + 1);
    }

    previous() {
      this.select(this.index - 1);
    }

    /* ---------- auto rotate ---------- */

    startAuto() {
      if (!this.autoRotate || this.timer || this.paused || !this.visible) return;
      if (document.hidden) return;
      var self = this;
      this.timer = window.setInterval(function () {
        self.select(self.index + 1, true);
      }, this.rotateInterval);
    }

    stopAuto() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartAuto() {
      this.stopAuto();
      this.startAuto();
    }

    /* ---------- events ---------- */

    handlePanelClick(event) {
      // Let real links and buttons inside a panel do their job.
      if (event.target.closest('a, button')) return;
      var panel = event.currentTarget;
      this.select(this.panels.indexOf(panel));
    }

    handlePanelKey(event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      if (event.target.closest('a, button')) return;
      event.preventDefault();
      this.select(this.panels.indexOf(event.currentTarget));
    }

    handlePanelEnter(event) {
      if (window.matchMedia('(max-width: 767px)').matches) return;
      this.select(this.panels.indexOf(event.currentTarget), true);
    }

    handlePrev(event) {
      event.preventDefault();
      this.previous();
    }

    handleNext(event) {
      event.preventDefault();
      this.next();
    }

    handleDot(event) {
      event.preventDefault();
      var i = parseInt(event.currentTarget.getAttribute('data-gallery-dot'), 10);
      if (!isNaN(i)) this.select(i);
    }

    handleKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      }
    }

    /* Hovering the panel background or a card off to the side isn't reading —
       it's just where the cursor happens to be. Rotation only holds while the
       pointer is over the product being shown or something you can act on. */
    handleHover(event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var overControl = target.closest(
        '[data-gallery-prev], [data-gallery-next], [data-gallery-dot], ' +
          '[data-caption-index], a, button'
      );

      var panel = target.closest('.gallery-panel');
      var overActive = panel && (panel.classList.contains('is-active') || panel.classList.contains('active'));

      if (overControl || overActive) this.handlePause();
      else this.handleResume();
    }

    handlePause() {
      this.paused = true;
      this.stopAuto();
    }

    handleResume() {
      this.paused = false;
      this.startAuto();
    }

    handleTouchStart(event) {
      if (!event.touches || !event.touches.length) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    }

    handleTouchEnd(event) {
      if (this.touchStartX == null) return;
      if (!event.changedTouches || !event.changedTouches.length) return;

      var deltaX = event.changedTouches[0].clientX - this.touchStartX;
      var deltaY = event.changedTouches[0].clientY - this.touchStartY;

      this.touchStartX = null;
      this.touchStartY = null;

      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      if (this.isFlat) return; // flat scrolls natively on mobile

      if (deltaX < 0) this.next();
      else this.previous();
    }

    handleDocumentVisibility() {
      if (document.hidden) this.stopAuto();
      else this.startAuto();
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;
      var panel = event.target.closest('.gallery-panel');
      if (!panel) return;
      this.handlePause();
      this.select(this.panels.indexOf(panel), true);
    }

    handleBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.handleResume();
    }
  }

  if (!customElements.get('expanding-gallery')) {
    customElements.define('expanding-gallery', ExpandingGallery);
  }
})();